// Explore › Powers of Ten (ba-web's docs/areas/web-powers-journey.md; engines/wgpu #778, 85801eea): the viewport's native
// 30 s dive from a volunteer's head (engine #1058) through the whole brain and one of H01's neurons and its synapse to one NMDA receptor (#928), drawn over the scene. The view's preset names the journey
// (ScenePreset.journey); this store runs it: started once the brain is in and the viewport lists it (`ready.journeys`),
// driven by the scene's clock through setTime alone (never per-frame commands: the engine asks for its own frames,
// #2 5966053716), its stages on the transport and the scale ruler, stopped to look around (the camera handed to the
// pointer, the stage's data kept), resumed to the path, and ended when the view closes. A viewport without journeys
// (the 2D preview, an older build) says so in the HUD and the panel, and the Views list leaves the view out.

import { create } from 'zustand';
import { useAtlas } from '../app/store.ts';
import { journeyOf } from '../app/scene.ts';
import { scenarioById } from '../scenarios/index.ts';
import { getViewport } from '../viewport/host.ts';
import type { JourneyReply, JourneyStage, Orbit, ViewportEvent } from '../viewport/api.ts';

export type JourneyStatus = 'off' | 'waiting' | 'starting' | 'on' | 'stopped' | 'unavailable' | 'error';

export interface JourneyState {
  /** The journey the view asks for, and the view's name (the HUD's and the panel's word for it). */
  id: string | null;
  name: string | null;
  status: JourneyStatus;
  /** Why it isn't playing: a viewport without journeys, or the engine's refusal. */
  why: string | null;
  /** The viewport's reply once it has started: the stages, the data it streams, what its scale measures. */
  reply: JourneyReply | null;
  /** The stage the last event named; null before the first. */
  stage: string | null;
  /** The first stage's files streaming in (viewport `progress` of 'journey') while the start runs. */
  progress: { loaded: number; total: number } | null;
  /** Where it stopped (s), while it's stopped. */
  stoppedAt: number | null;
  /** A link's camera (url.ts `cam`): the journey opens stopped there, looking around at that scale. */
  openCamera: Orbit | null;
}

export interface JourneyActions {
  /** Stop where it is, the camera handed to the pointer (`keepCamera`); the clock pauses. */
  stop(): Promise<void>;
  /** Back to the path from the clock's time (or `from`), blending over 0.6 s. */
  resume(from?: number): Promise<void>;
  /** Look around, or back to the path and on with the film. */
  toggleLook(): void;
  /** Play from a moment (a stage's start): resumes a stopped journey there. */
  jumpTo(t: number): void;
  setOpenCamera(o: Orbit | null): void;
}

export type JourneyStore = JourneyState & JourneyActions;

const INITIAL: JourneyState = { id: null, name: null, status: 'off', why: null, reply: null, stage: null, progress: null, stoppedAt: null, openCamera: null };

/** The last journey event, for the ruler's marker and the checks: it comes up to once a frame, so it stays out of the store. */
export const lastJourney: { current: Extract<ViewportEvent, { ev: 'journey' }> | null } = { current: null };

export const UNAVAILABLE_PREVIEW = 'The 2D preview doesn’t play journeys: Powers of Ten needs the 3D viewport';
export const UNAVAILABLE_BUILD = 'This viewport build has no journeys; Powers of Ten needs one that lists it';

export const reason = (err: unknown): string => (err instanceof Error ? err.message : String((err as { error?: unknown } | null)?.error ?? err));

/** The journey's reply as the viewport sends it, or why it isn't one. */
export function asReply(r: unknown): JourneyReply {
  const x = r as Partial<JourneyReply> | null;
  if (!x || typeof x.durationS !== 'number' || !Array.isArray(x.stages) || !x.stages.length) throw new Error('the viewport’s reply names no stages');
  return { durationS: x.durationS, stages: x.stages, data: Array.isArray(x.data) ? x.data : [], scale: typeof x.scale === 'string' ? x.scale : '' };
}

export const useJourney = create<JourneyStore>()((set, get) => ({
  ...INITIAL,
  async stop() {
    const { id, status } = get();
    if (!id || status !== 'on') return;
    const vp = await getViewport();
    if (!vp || get().status !== 'on') return;
    try {
      const r = (await vp.send({ cmd: 'journey', id, on: false, keepCamera: true })) as { t?: number };
      const a = useAtlas.getState();
      a.setPlaying(false);
      // The clock takes the moment it stopped at; the viewport, stopped, ignores the setTime this sends.
      if (typeof r.t === 'number') a.setTime(r.t);
      const at = typeof r.t === 'number' ? r.t : a.time;
      // Stopped at once after the start (a link's camera), no event has named the stage yet: the reply says which it is.
      set({ status: 'stopped', stoppedAt: at, stage: get().stage ?? stageAt(get().reply, at) });
    } catch (err) {
      set({ status: 'error', why: reason(err) });
    }
  },
  async resume(from) {
    const { id, status } = get();
    if (!id || status !== 'stopped') return;
    set({ status: 'on', stoppedAt: null });
    const vp = await getViewport();
    if (!vp) return;
    const t = from ?? useAtlas.getState().time;
    try {
      await vp.send({ cmd: 'journey', id, on: true, from: t });
    } catch (err) {
      set({ status: 'error', why: reason(err) });
    }
  },
  toggleLook() {
    const { status } = get();
    if (status === 'on') void get().stop();
    else if (status === 'stopped') {
      void get().resume();
      useAtlas.getState().setPlaying(true);
    }
  },
  jumpTo(t) {
    const a = useAtlas.getState();
    a.setTime(t);
    if (get().status === 'stopped') void get().resume(t);
    a.setPlaying(true);
  },
  setOpenCamera(o) {
    set({ openCamera: o });
  },
}));

/** The stage a moment falls in (the last stage keeps its end), or null outside the journey. */
export function stageAt(reply: Pick<JourneyReply, 'stages'> | null, t: number): string | null {
  if (!reply) return null;
  const last = reply.stages.at(-1);
  if (last && t >= last.t1) return last.id;
  return reply.stages.find((st) => t >= st.t0 && t < st.t1)?.id ?? null;
}

/** The view's height at a moment, from the reply: log-interpolated inside its stage, as the ruler interpolates (spec §2). */
export function scaleAt(reply: Pick<JourneyReply, 'stages'> | null, t: number): number | null {
  const id = stageAt(reply, t);
  const st = reply?.stages.find((x) => x.id === id);
  if (!st) return null;
  const f = st.t1 > st.t0 ? Math.min(1, Math.max(0, (t - st.t0) / (st.t1 - st.t0))) : 0;
  return st.scaleMm[0] * (st.scaleMm[1] / st.scaleMm[0]) ** f;
}

/** Back to nothing: the view closed. */
export function resetJourney(): void {
  lastJourney.current = null;
  useJourney.setState({ ...INITIAL, openCamera: useJourney.getState().openCamera });
}

/** The view's name for the journey (Explore's "Powers of Ten"), from the preset that names it. */
export function journeyName(id: string): string {
  for (const sc of [scenarioById(useAtlas.getState().scenario)]) {
    const p = sc.presets.find((x) => x.journey?.id === id);
    if (p) return p.name;
  }
  return id;
}

/** Whether the view on screen is a journey (its preset names one). */
export const journeyActive = (s: Parameters<typeof journeyOf>[0]): boolean => journeyOf(s) !== null;

/** A scale as the ruler reads it: mm to three figures, µm below a millimetre, nm below a micrometre (#928's synapse and
 *  receptor), as the scale bar does (features/scaleBar.ts). */
export function formatScale(mm: number): string {
  if (mm >= 1) return `${+mm.toPrecision(3)} mm`;
  return mm >= 1e-3 ? `${+(mm * 1e3).toPrecision(3)} µm` : `${+(mm * 1e6).toPrecision(3)} nm`;
}

/** Where a scale sits on a log ruler from `hi` (top, 0) to `lo` (bottom, 1). */
export function rulerFrac(scaleMm: number, hi: number, lo: number): number {
  if (!(hi > lo) || !(scaleMm > 0)) return 0;
  return Math.min(1, Math.max(0, Math.log(hi / scaleMm) / Math.log(hi / lo)));
}

/** The decades inside the ruler's range (100 mm, 10 mm, 1 mm, on down to 10 nm), largest first. */
export function rulerDecades(hi: number, lo: number): number[] {
  const out: number[] = [];
  for (let e = 3; e >= -6; e--) {
    const v = 10 ** e;
    if (v < hi && v > lo) out.push(v);
  }
  return out;
}

/**
 * Which stops carry their names on a ruler `heightPx` tall: the stage on screen (`current`) first, whose name always
 * shows, then the longer stages, and a name only where no other shown name is within `minGapPx`. The dissolves
 * (0.8 s, a few per cent of the ruler) keep their dot, and so do the top end's stops beside the stage on screen:
 * the head's four come 10–14 px apart on a laptop's ruler.
 */
export function namedStops(stages: JourneyStage[], hi: number, lo: number, heightPx: number, current: string | null = null, minGapPx = 16): Set<string> {
  const shown = new Set<string>();
  const taken: number[] = [];
  const ordered = [...stages].sort((a, b) => Number(b.id === current) - Number(a.id === current) || b.t1 - b.t0 - (a.t1 - a.t0) || a.t0 - b.t0);
  for (const st of ordered) {
    const y = rulerFrac(st.scaleMm[0], hi, lo) * heightPx;
    if (taken.some((t) => Math.abs(t - y) < minGapPx)) continue;
    taken.push(y);
    shown.add(st.id);
  }
  return shown;
}

/** The data a stage draws, for the caption: BigBrain 2015 through the histology, H01 from the tissue on, both across the dissolve. */
export function stageData(stage: Pick<JourneyStage, 'id' | 'methods'>): string {
  if (stage.id === 'histology-h01-dissolve') return 'BigBrain 2015 · H01';
  // The top end (engine #1058): IXI 165's head, skull and brain, then the dissolve into BigBrain's.
  if (stage.id === 'ixi165-bigbrain-dissolve') return 'IXI 165 · BigBrain 2015';
  if (stage.id.startsWith('ixi165-')) return 'IXI 165 (IXI dataset)';
  if (stage.id.startsWith('h01-')) return 'H01 (Shapson-Coe 2024)';
  if (stage.id.startsWith('hd-cortex') || stage.id.startsWith('cortex-') || stage.id.startsWith('bigbrain-')) return 'BigBrain 2015 (Amunts)';
  // An unknown stage: its line's own provenance word.
  return provenanceOf(stage.methods) ?? 'the engine’s data';
}

/** The provenance word a stage's Methods line ends in (" · reference", " · reference, measured"). */
export function provenanceOf(methods: string): string | null {
  const i = methods.lastIndexOf(' · ');
  return i > 0 ? methods.slice(i + 3).trim() : null;
}

/** The Methods caption (the pill is short; the panel's section is titled with the view): the stage on screen and its data. */
export function journeyCaption(name: string | null, reply: JourneyReply | null, stageId: string | null): string {
  const st = reply?.stages.find((x) => x.id === stageId);
  return st ? `${st.name} · ${stageData(st)}` : `${name ?? 'Powers of Ten'} · BigBrain 2015 at 100 µm · H01`;
}

/** The HUD's line while the journey starts, or why it isn't playing. */
export function journeyNote(s: Pick<JourneyState, 'status' | 'why' | 'progress' | 'name'>): string | null {
  const who = s.name ?? 'Powers of Ten';
  if (s.status === 'starting') return s.progress ? `Streaming ${who}’s first stage, ${s.progress.loaded} of ${s.progress.total}` : `Starting ${who}…`;
  if (s.status === 'unavailable') return s.why ?? `${who} isn’t available in this viewport`;
  if (s.status === 'error') return `${who} didn’t start: ${s.why ?? 'unknown'}`;
  return null;
}

/** The panel's status line: what the visitor is waiting for, or why nothing plays. */
export function journeyStatusLine(s: Pick<JourneyState, 'status' | 'why' | 'progress' | 'name'>): string | null {
  if (s.status === 'waiting') return 'Opens once the brain is in…';
  if (s.status === 'starting') return s.progress ? `Streaming the first stage, ${s.progress.loaded} of ${s.progress.total} files…` : 'Starting, the first stage’s data on its way…';
  return journeyNote(s);
}

export const JOURNEY_METHODS =
  'Powers of Ten is the engine’s native 30-second dive (engines/wgpu #778, #928), drawn here over Explore’s template brain and driven by this clock: from a volunteer’s head at a 319 mm view to one receptor at 24 nm, through fifteen stages. The head is IXI 165’s from MRI (the IXI dataset), its face removed and shown as a sculpture; it clears to its skull and its brain, which dissolves into BigBrain’s, another person’s, in the same space. The whole brain is BigBrain 2015’s own pial surface (Amunts et al. 2013), carried into MNI152 2009c through its recorded template warp. A coronal cut opens BigBrain’s histology at 100 µm, 64³ bricks ray-marched at 0.15 mm, the cut face in a measured Merker-stain display LUT. The histology then dissolves into H01 (Shapson-Coe, Januszewski, Berger et al. 2024), electron microscopy of a cubic millimetre of human temporal cortex: a separate specimen shown beside the cut at the same scale, not registered to it; its 57,215 cells in their layers, the skeletons of its 104 proofread cells simplified to 1 µm, and one of them with its synapses. Past the neuron come one of its excitatory synapses, the axon’s bouton and vesicles over its spine from H01’s segmentation and synapse annotation; the synaptic cleft, over NMDA receptors that are copies of one deposited structure (PDB 6IRA), their number, places and turns artist-made, since electron microscopy cannot show receptors; and one receptor, PDB 6IRA’s molecular surface (Zhang et al. 2018) with glutamate and glycine placed from PDB 5H8F. Each stage’s line ends in its provenance word: reference for BigBrain and the receptor, measured for H01, derived for the synapse, artist-made for the receptors’ placing. The scale ruler reads the view’s height at the camera’s target, in millimetres, micrometres and nanometres, from the engine’s own events, so it is true at every frame; the camera path, the dissolves and the framing are the engine’s. Stopped, the camera is yours and the stage’s data stay resident; resumed, it blends back to the path over 0.6 s. First play streams about 400 MB (BigBrain’s pial surface, about 270 MB of 100 µm bricks across the view’s cut, about 110 MB of H01, about 10 MB for the synapse and the receptor), each file once; the picture fills in as the bricks arrive.';

/** About how much a first play streams, for the panel's note. */
export const JOURNEY_STREAM_NOTE = 'First play streams about 400 MB: IXI 165’s head, BigBrain 2015’s pial surface and 100 µm histology, then H01, its synapse and one receptor. The picture fills in as the bricks arrive; later plays come from the browser’s cache.';

// The checks read the journey here: the store, and the last event the viewport sent.
if (typeof window !== 'undefined') (window as unknown as { __journey?: { store: typeof useJourney; last: typeof lastJourney } }).__journey = { store: useJourney, last: lastJourney };
